import React, { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";

import LoginPage from "./pages/Login";
import RegisterPage from "./pages/Register";
import ErrorPage from "./pages/Error";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ChangePasswordPage from "./pages/ChangePassword";
import FAQPage from "./pages/Legal/FAQ";
import TermsConditionPage from "./pages/Legal/TermsConditions";
import PrivacyPolicyPage from "./pages/Legal/PrivacyPolicy";
import AccessibilityPage from "./pages/Legal/Accessibility";
import ResetPasswordPage from "./pages/ResetPassword";
import UnlockAccountPage from "./pages/UnlockAccount";
import UnlockAccountFromTokenPage from "./pages/UnlockAccountFromToken";
import MaintenancePage from "./pages/Maintenance";
import UnauthorizedPage from "./pages/Unauthorized";
import BrowserSupportPage from "./pages/BrowserSupport";
import RequireAuth from "./features/auth/components/RequireAuth";
import {
  TableSkeleton,
  FormSkeleton,
  CardSkeleton,
  InvoiceDetailsSkeleton as PageDetailsSkeleton,
} from "./components/Skeleton";
import AppFormWithCustomConfig from "./pages/Application/AppFormWithCustomConfig";
import VerifyEmail from "./pages/Register/VerifyEmail";
import VerifyAccount from "./pages/VerifyAccount";
import SSOVerificationStatus from "./pages/VerifyAccount/SSOVerificationStatus";
import CallbackPage from "./pages/Callback";
import ErrorBoundary from "./ErrorBoundary";
import { isSSOEnabled } from "./utils/helpers";

const LicenseDashboard = lazy(() => import("./pages/License/List"));
const LicenseDetailsPage = lazy(() => import("./pages/License/Details"));

const InvoicesDashboard = lazy(() => import("./pages/Invoices/List"));
const InvoiceDetailsPage = lazy(() => import("./pages/Invoices/Details"));
const ApplicationsPage = lazy(() => import("./pages/Application/List"));
const ApplicationDetailsPage = lazy(() =>
  import("./pages/Application/Details")
);
const PaymentStatusPage = lazy(() =>
  import("./features/invoices/components/PaymentStatus")
);
const AccountSettingsPage = lazy(() => import("./pages/AccountSettings"));
const ControlPanelAccountsPage = lazy(() =>
  import("./pages/ControlPanel/Accounts")
);
const ControlPanelRelationshipsPage = lazy(() =>
  import("./pages/ControlPanel/Relationships")
);
const ControlPanelUserListing = lazy(() =>
  import("./pages/ControlPanel/Users")
);
const ControlPanelUserProfile = lazy(() =>
  import("./pages/ControlPanel/Users/iamUsersProfile")
);

const registerRoute = {
  path: "register",
  element: (
    <Suspense fallback={<FormSkeleton />}>
      <ErrorBoundary key="register">
        <RegisterPage />
      </ErrorBoundary>
    </Suspense>
  ),
  documentTitle: "Register",
};

const loggedOutRoutes = [
  {
    path: "login",
    element: (
      <ErrorBoundary key="login">
        <LoginPage />
      </ErrorBoundary>
    ),
    documentTitle: "Login",
  },
  {
    path: "forgot-password",
    element: (
      <ErrorBoundary key="forgotPassword">
        <ForgotPasswordPage />
      </ErrorBoundary>
    ),
    documentTitle: "Forgot Password",
  },
  {
    path: "resetPassword",
    element: (
      <ErrorBoundary key="resetPassword">
        <ResetPasswordPage />
      </ErrorBoundary>
    ),
    documentTitle: "Reset Password",
  },
  {
    path: "unlockAccount",
    element: (
      <ErrorBoundary key="unlockAccount">
        <UnlockAccountPage />
      </ErrorBoundary>
    ),
    documentTitle: "Unlock Account",
  },
  {
    path: "unlockAccountFromEmail",
    element: (
      <ErrorBoundary key="unlockAccountFromToken">
        <UnlockAccountFromTokenPage />
      </ErrorBoundary>
    ),
    documentTitle: "Unlock Account",
  },
  {
    path: "resendVerifyAccountEmail",
    element: (
      <ErrorBoundary key="resendVerifyAccountEmail">
        <VerifyEmail />
      </ErrorBoundary>
    ),
    documentTitle: "Resend Verification Email",
  },
  {
    path: "verifyAccount",
    element: (
      <ErrorBoundary key="verifyAccount">
        <VerifyAccount />
      </ErrorBoundary>
    ),
    documentTitle: "Verify Account",
  },
  {
    path: "oidc-verification/:status",
    element: (
      <ErrorBoundary key="ssoVerificationStatus">
        <SSOVerificationStatus />
      </ErrorBoundary>
    ),
    documentTitle: "Verification Status",
  },
  {
    path: "callback/:status",
    documentTitle: "Login Callback",
    element: (
      <ErrorBoundary key="callback">
        <CallbackPage />
      </ErrorBoundary>
    ),
  },
];

const loggedInRoutes = [
  {
    path: "applications",
    element: (
      <Suspense fallback={<TableSkeleton />}>
        <ErrorBoundary key="applicationListing">
          <ApplicationsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Applications",
    breadcrumb: {
      label: { value: "Applications", type: "string" },
      hidden: true,
    },
  },
  {
    path: "applications/:applicationType/:applicationId",
    element: (
      <Suspense fallback={<PageDetailsSkeleton />}>
        <ErrorBoundary key="applicationDetail">
          <ApplicationDetailsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Application Details",
    breadcrumb: {
      label: { value: "applicationType", type: "params" },
      parentPath: "applications",
      showBorder: true,
    },
  },
  {
    exact: true,
    path: "application-forms/:applicationType/:applicationId/payment-status",
    element: (
      <Suspense fallback={<CardSkeleton />}>
        <ErrorBoundary key="paymentStatusPage">
          <PaymentStatusPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Application Payment Status",
    breadcrumb: {
      label: { value: "Payment Status", type: "string" },
      showBorder: true,
    },
  },
  {
    path: "licenses",
    element: (
      <Suspense fallback={<TableSkeleton />}>
        <ErrorBoundary key="licenseListing">
          <LicenseDashboard />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Licenses",
    breadcrumb: {
      label: {
        value: "Licenses",
        type: "navItemMapping",
        systemName: "licenses",
      },
      hidden: true,
    },
  },
  {
    path: "licenses/:recordType/:licenseNumber/:recordId",
    element: (
      <Suspense fallback={<PageDetailsSkeleton />}>
        <ErrorBoundary key="licenseDetail">
          <LicenseDetailsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "License Details",
    breadcrumb: {
      label: { value: "recordType", type: "params" },
      parentPath: "licenses",
      showBorder: true,
    },
  },
  {
    path: "/",
    element: <Navigate to="/licenses" />,
  },
];

const invoiceRoutes = [
  {
    path: "invoices",
    element: (
      <Suspense fallback={<TableSkeleton />}>
        <ErrorBoundary key="invoiceListing">
          <InvoicesDashboard />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Invoices",
    breadcrumb: {
      label: { value: "Invoices", type: "string" },
      hidden: true,
    },
  },
  {
    path: "invoices/:id",
    exact: true,
    element: (
      <Suspense fallback={<PageDetailsSkeleton />}>
        <ErrorBoundary key="invoiceDetail">
          <InvoiceDetailsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Invoice Details",
    breadcrumb: {
      label: { value: "id", type: "params" },
      parentPath: "invoices",
      showBorder: true,
    },
  },
  {
    path: "invoices/:type/:invoiceId/payment-status",
    exact: true,
    element: (
      <Suspense fallback={<CardSkeleton />}>
        <ErrorBoundary key="paymentStatusPage">
          <PaymentStatusPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Invoice Payment Status",
    breadcrumb: {
      label: { value: "Payment Status", type: "string" },
    },
  },
];

const accountRoutes = [
  {
    path: "accounts/:tenantId/:type",
    element: (
      <Suspense fallback={<FormSkeleton />}>
        <ErrorBoundary key="AccountSetting">
          <AccountSettingsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Account Settings",
    breadcrumb: {
      label: { value: "Details", type: "string" },
      parent: {
        path: "accounts/:tenantId/:type",
        breadcrumb: {
          label: { value: "Account Settings", type: "string" },
        },
      },
      showBorder: true,
    },
  },
  {
    path: "tenants/:tenantId/:type",
    element: (
      <Suspense fallback={<FormSkeleton />}>
        <ErrorBoundary key="AccountInfo">
          <AccountSettingsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Account Information",
    breadcrumb: {
      label: { value: "Account Information", type: "string" },
      parent: {
        path: "tenants",
        breadcrumb: {
          label: { value: "Accounts", type: "string" },
        },
      },
    },
  },
];

const controlRoutes = [
  {
    path: "tenants",
    element: (
      <Suspense fallback={<TableSkeleton />}>
        <ErrorBoundary key="controlPanelAccounts">
          <ControlPanelAccountsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Accounts",
    breadcrumb: {
      label: { value: "Accounts", type: "string" },
      hidden: true,
    },
  },
  {
    path: "users",
    element: (
      <Suspense fallback={<TableSkeleton />}>
        <ErrorBoundary key="controlPanelRelationships">
          <ControlPanelRelationshipsPage />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Relationships",
  },
  {
    path: "iamusers",
    element: (
      <Suspense fallback={<TableSkeleton />}>
        <ErrorBoundary key="controlPanelUserListing">
          <ControlPanelUserListing />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "Users",
    breadcrumb: {
      label: { value: "Users", type: "string" },
      hidden: true,
    },
  },
  {
    path: "iamusers/:userHref",
    element: (
      <Suspense fallback={<FormSkeleton />}>
        <ErrorBoundary key="controlPanelUserProfile">
          <ControlPanelUserProfile />
        </ErrorBoundary>
      </Suspense>
    ),
    documentTitle: "User Details",
    breadcrumb: {
      label: { value: "User Details", type: "string" },
      parentPath: "iamusers",
    },
  },
];

const loggedInRoutesWithoutPermission = [
  {
    path: "maintenance",
    element: <MaintenancePage />,
    documentTitle: "Under Maintenance",
    exact: true,
  },
];

if (isSSOEnabled) {
  loggedInRoutesWithoutPermission.push(registerRoute);
} else {
  loggedInRoutes.push({
    path: "change-password",
    element: (
      <ErrorBoundary key="changePassword">
        <ChangePasswordPage />
      </ErrorBoundary>
    ),
    documentTitle: "Change Password",
  });
  loggedOutRoutes.push(registerRoute);
}

const routes = [
  {
    path: "app-form",
    element: <AppFormWithCustomConfig />,
    documentTitle: "App Form",
  },
  //  public routes
  {
    path: "faq",
    element: (
      <ErrorBoundary key="faq">
        <FAQPage />
      </ErrorBoundary>
    ),
    documentTitle: "Frequently Asked Questions",
  },
  {
    path: "terms-conditions",
    element: (
      <ErrorBoundary key="terms-conditions">
        <TermsConditionPage />
      </ErrorBoundary>
    ),
    documentTitle: "Terms & Conditions",
  },
  {
    path: "privacy-policy",
    element: (
      <ErrorBoundary key="privacy-policy">
        <PrivacyPolicyPage />
      </ErrorBoundary>
    ),
    documentTitle: "Privacy Policy",
  },
  {
    path: "accessibility",
    element: (
      <ErrorBoundary key="accessibility">
        <AccessibilityPage />
      </ErrorBoundary>
    ),
    documentTitle: "Accessibility",
  },
  {
    path: "browser-support",
    element: (
      <ErrorBoundary key="browser-support">
        <BrowserSupportPage />
      </ErrorBoundary>
    ),
    documentTitle: "Supported Browser",
  },
  { path: "error", element: <ErrorPage />, documentTitle: "" },
  {
    path: "unauthorized",
    element: <UnauthorizedPage />,
    documentTitle: "Unauthorized",
  },

  // routes accessible only when user is logged out
  {
    element: <RequireAuth showWhenUserLoggedOut />,
    children: loggedOutRoutes,
  },

  // maintenance page route
  {
    element: <RequireAuth />,
    children: loggedInRoutesWithoutPermission,
  },

  // routes accessible only when user is logged in
  {
    element: (
      <RequireAuth
        allowedItems={["allPermissions", "allTenantPermissions"]}
        navConfigItem
      />
    ),
    children: loggedInRoutes,
  },

  // invoice route
  {
    element: <RequireAuth conditionalNavConfigItem />,
    children: invoiceRoutes,
  },

  {
    element: (
      <RequireAuth allowedItems={["allPermissions", "allTenantPermissions"]} />
    ),
    children: accountRoutes,
  },

  {
    element: (
      <RequireAuth allowedItems={["allPermissions", "view_control_panel"]} />
    ),
    children: controlRoutes,
  },
];

export default routes;
